import Popover from "@material-ui/core/Popover";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import {ConfirmationPrompt} from "../../ui/ConfirmationPrompt";
import {useApiNotification} from "../../ui/ApiNotificationProvider";
import {removeMember} from "../../../api/organization-unit-functions";
import {run} from "../../../utils/function-util";
import {Collapse} from "@material-ui/core";
import {ExpandLess, ExpandMore} from "@material-ui/icons";
import {MultipleSubscriptionType} from "./membership-fields/MultipleSubscriptionType";
import {resendNotificationForPendingConsent} from "../../../api/subscriptions-functions";
import * as _ from 'lodash';
import {MoveMemberAction} from "../../members/shared/actions/MoveMemberAction";

export const MembershipActions = ({
                                      userId,
                                      subscriptions,
                                      organizationId,
                                      organizationUnitId,
                                      isActive,
                                      ownershipStatus,
                                      onActionCompleted
                                  }: any) => {

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isOpen = Boolean(anchorEl);

    return (
        <div>
            <MoreVertIcon onClick={handleClick}/>
            <Popover
                id={`actions-over-user-${userId}`}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}>
                <MembershipActionsMenu userId={userId}
                                       organizationId={organizationId}
                                       organizationUnitId={organizationUnitId}
                                       isActive={isActive}
                                       subscriptions={subscriptions}
                                       onActionCompleted={onActionCompleted}
                                       ownershipStatus={ownershipStatus}
                                       onCancel={handleClose}
                />
            </Popover>
        </div>
    )
};

const MembershipActionsMenu = ({
                                   userId,
                                   subscriptions,
                                   organizationId,
                                   organizationUnitId,
                                   isActive,
                                   onActionCompleted,
                                   ownershipStatus,
                                   onCancel
                               }: any) => {
    const displayResendEmailNotification = !isActive;
    return (
        <List>
            {displayResendEmailNotification &&
            <ResendEmailNotification
                userId={userId}
                organizationId={organizationId}
                organizationUnitId={organizationUnitId}
                onClicked={onCancel}
            />
            }
            <DeleteUserMembership
                userId={userId}
                organizationId={organizationId}
                organizationUnitId={organizationUnitId}
                subscriptions={subscriptions}
                onUserDeleted={onActionCompleted}
                ownershipStatus={ownershipStatus}
                onCancel={onCancel}
            />
            {subscriptions.length > 0 &&
            <RemoveUserSubscription
                organizationId={organizationId}
                organizationUnitId={organizationUnitId}
                subscriptions={subscriptions}
                onSubscriptionRemoved={onActionCompleted}
            />
            }
            <MoveMemberAction organizationId={organizationId}
                              member={{userId: userId, organizationUnitId: organizationUnitId}}
                              onMoved={onActionCompleted}
                              onActionCompleted={onCancel}/>

        </List>
    )
};

const ResendEmailNotification = ({
                                     organizationId,
                                     organizationUnitId,
                                     userId,
                                     onClicked
                                 }: any) => {
    const {t} = useTranslation();
    const {handleError, showSuccess} = useApiNotification();

    const resendEmailNotification = async () => {
        onClicked();
        try {
            await resendNotificationForPendingConsent(organizationId, organizationUnitId, userId);
        } catch (e) {
            const error: any = _.get(e, 'response.data.error');
            if (error && error.type === 'CannotResendPendingConsentNotificationNow') {
                const customMessage = t('_Common.Api.Messages.CannotResendPendingConsentNotificationNow')
                    .replace('{0}', error.data.timeToWait);
                handleError({customMessage});
            } else {
                handleError(e);
            }
            return;
        }
        showSuccess(t('_Common.Api.Messages.EmailResent'));
    }

    return (
        <ListItem>
            <ListItemText
                style={{cursor: 'pointer'}}
                onClick={resendEmailNotification}>
                {t('_Common.Labels.ResendEmailNotification')}
            </ListItemText>
        </ListItem>
    )
}


const DeleteUserMembership = ({
                                  userId,
                                  subscriptions,
                                  organizationId,
                                  organizationUnitId,
                                  onUserDeleted,
                                  ownershipStatus,
                                  onCancel
                              }: any) => {
    const {t} = useTranslation();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const {handleError} = useApiNotification();
    const [disableDelete, setDisableDelete] = useState(false);

    const deleteUser = async () => {
        setDisableDelete(true);
    };

    useEffect(run(
        async () => {
            if (disableDelete) {
                return await removeMember(organizationId, organizationUnitId, userId)
            }
        },
        () => {
            if (disableDelete) {
                setShowConfirmation(false);
                onUserDeleted(userId);
            }
        },
        handleError,
        () => {
            if (disableDelete) {
                setDisableDelete(false);
                onCancel();
            }
        }), [disableDelete])

    const hasSubscriptionsFromCurrentOrganizationUnit = subscriptions && subscriptions.some((s: any) => s.source === 'OrganizationUnit');

    const isOwned = ownershipStatus === 'Active';

    const confirmationMessage = isOwned ?
        (hasSubscriptionsFromCurrentOrganizationUnit ?
            t(`Subscription.Membership.DeleteMembership.ConfirmationMessage.IsOwned.HasSubscriptions`)
            : t(`Subscription.Membership.DeleteMembership.ConfirmationMessage.IsOwned.HasNoSubscriptions`))
        : (hasSubscriptionsFromCurrentOrganizationUnit ?
            t(`Subscription.Membership.DeleteMembership.ConfirmationMessage.IsNotOwned.HasSubscriptions`)
            : t(`Subscription.Membership.DeleteMembership.ConfirmationMessage.IsNotOwned.HasNoSubscriptions`));

    return (
        <ListItem>
            <ListItemText
                style={{cursor: 'pointer'}}
                onClick={() => setShowConfirmation(true)}>
                {t('_Common.Labels.RemoveUser')}
            </ListItemText>
            <ConfirmationPrompt
                title={t(`Subscription.Membership.DeleteMembership.Title`)}
                description={confirmationMessage}
                confirmationButtonKey={t('_Common.Labels.Confirm')}
                open={showConfirmation}
                disable={disableDelete}
                onCancel={() => {
                    onCancel();
                    setShowConfirmation(false);
                }}
                onConfirm={() => deleteUser()}
            />
        </ListItem>
    )
};

const RemoveUserSubscription = ({
                                    subscriptions,
                                    organizationId,
                                    organizationUnitId,
                                    onSubscriptionRemoved
                                }: any) => {
    const {t} = useTranslation();
    const [initRender, setInitRender] = React.useState(true);
    const [openCollapsable, setOpenCollapsable] = React.useState(true);

    const handleClickCollapsable = () => {
        setOpenCollapsable(!openCollapsable);
    };

    useEffect(() => {
        if (initRender) {
            setInitRender(false);
            setOpenCollapsable(false);
        }
    });

    return (
        <div>
            <ListItem button onClick={handleClickCollapsable}>
                <ListItemText style={{width: '200px'}}>
                    {t('_Common.Labels.RemoveSubscription')}
                </ListItemText>
                {openCollapsable ? <ExpandLess/> : <ExpandMore/>}
            </ListItem>
            <Collapse in={openCollapsable}>
                <MultipleSubscriptionType subscriptions={subscriptions}
                                          organizationId={organizationId}
                                          organizationUnitId={organizationUnitId}
                                          onSubscriptionRemoved={onSubscriptionRemoved}/>
            </Collapse>
        </div>
    )
};
